import React, { Component } from "react";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  Badge,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";
import moment from "moment";
import { normalizeWebLanguage } from "../../webLanguages";
import "moment/locale/fr";
import "moment/locale/de";
import { withNamespaces } from "react-i18next";
import { NotificationDatabaseConnector } from "../../dataconnector/notificationDatabaseConnector";
import { ActivityDatabaseConnector } from "../../dataconnector/activityDatabaseConnector";
import ActivityQuickAddModal from "../Activities/ActivityQuickAddModal";
import ActivityAlertsOverlay from "../Activities/ActivityAlertsOverlay";
import { buildActivityPrefillFromNotification } from "../Activities/activityFromNotification";
import {
  LazyTaskCreationModal,
  buildTaskPrefillFromNotification,
} from "../Activities/taskFromNotification";
import { activityDestination } from "../../activities-v2/core/createActivitiesCore";
import { buildBellNotificationRows } from "../Activities/notificationFollowUp";
import { isActivityWebAlertDue, isActivityWebAlertPending } from "../Activities/activityWebAlert";

const notifConnect = new NotificationDatabaseConnector();
const activityConnect = new ActivityDatabaseConnector();

/**
 * Header bell for the personal stream: "Mes rappels" (private activities)
 * on top, then in-app note notifications. Reminders live here — next to the
 * notifications they often originate from — rather than in the task bell,
 * which stays dedicated to team/sector work.
 *
 * Always visible (same as the task bell), regardless of the user's
 * notification channel. The badge counts unread notifications plus
 * reminders that are overdue or due today.
 */
class NoteNotificationDropdown extends Component {
  state = {
    notifications: [],
    dropdownOpen: false,
    unsubscribe: null,
    acknowledging: false,
    remindNotif: null, // notification being converted into a personal activity
    taskNotif: null, // notification being converted into a team task
    // Personal activities ("Mes rappels")
    activities: [],
    activityListener: null,
    activityModalOpen: false,
    editingActivity: null,
    activitiesExpanded: false,
    alertQueue: [], // ids of due reminders shown as stacked overlay cards
  };

  // Session guard against double alerts while the webNotifiedAt write
  // round-trips through the listener
  _alertedActivityIds = new Set();

  async componentDidMount() {
    const unsubscribe = await notifConnect.subscribeMyNoteNotifications(
      (notifications) => this.setState({ notifications })
    );
    this.setState({ unsubscribe });

    // Personal activities listener (private per-user collection)
    const activityListener = await activityConnect.subscribeMyActivities(
      (activities) => {
        this.setState((prev) => {
          // A rescheduled activity (webNotifiedAt reset) may alert again —
          // but never un-guard a card that is currently on screen
          activities.forEach((a) => {
            const id = a.activityId || a.id;
            if (!a.webNotifiedAt && !prev.alertQueue.includes(id)) {
              this._alertedActivityIds.delete(id);
            }
          });
          // Purge cards handled elsewhere (done, deleted, dismissed in
          // another tab, rescheduled)
          const byId = new Map(activities.map((a) => [a.activityId || a.id, a]));
          const alertQueue = prev.alertQueue.filter((id) => {
            const a = byId.get(id);
            return a && a.status === "open" && isActivityWebAlertPending(a);
          });
          return { activities, alertQueue };
        }, this.checkDueActivityAlerts);
      }
    );
    this.setState({ activityListener });
    // Desktop alert check every minute (browser notification + toast at due time)
    this._activityAlertInterval = setInterval(this.checkDueActivityAlerts, 60000);
  }

  componentWillUnmount() {
    if (typeof this.state.unsubscribe === "function") {
      this.state.unsubscribe();
    }
    if (typeof this.state.activityListener === "function") {
      this.state.activityListener();
    }
    if (this._activityAlertInterval) {
      clearInterval(this._activityAlertInterval);
    }
  }

  toggleDropdown = () => {
    this.setState((prev) => ({ dropdownOpen: !prev.dropdownOpen }));
  };

  closeDropdown = () => this.setState({ dropdownOpen: false });

  getUnread = () =>
    this.state.notifications.filter((n) => n.status !== "acknowledged");

  getLang = () =>
    (this.props.i18n && this.props.i18n.language
      ? this.props.i18n.language
      : "fr"
    ).substring(0, 2);

  localizedMoment = (m) => {
    if (!m) return null;
    const lang = this.getLang();
    m.locale(normalizeWebLanguage(lang));
    return m;
  };

  formatDate = (notif) => {
    let m = null;
    if (notif.noteTimestamp) m = moment.unix(notif.noteTimestamp);
    else if (notif.createdAt && notif.createdAt.toDate)
      m = moment(notif.createdAt.toDate());
    if (!m) return "";
    return this.localizedMoment(m).fromNow();
  };

  acknowledgeAll = async () => {
    const ids = this.getUnread().map((n) => n.id);
    if (ids.length === 0) return;
    this.setState({ acknowledging: true });
    await notifConnect.acknowledgeNotifications(ids);
    this.setState({ acknowledging: false });
  };

  // ---------- Personal activities ("Mes rappels") ----------

  /**
   * Desktop delivery of activity reminders: the primary audience (admins,
   * vets, techs) works on the web app, so the due-time alert fires here —
   * a persistent overlay card (stacked, dismissed manually) plus a native
   * browser notification if permission is granted. `webNotifiedAt` is
   * written once, on display; `webDismissedAt` on DISMISS, so an unhandled
   * card still comes back after a reload. The mobile push
   * (sendActivityReminders) remains as a fallback.
   */
  checkDueActivityAlerts = () => {
    const nowUnix = Math.floor(Date.now() / 1000);
    this.state.activities
      .filter(
        (a) =>
          isActivityWebAlertDue(a, nowUnix) &&
          !this._alertedActivityIds.has(a.activityId || a.id)
      )
      .forEach((a) => this.showActivityAlert(a));
  };

  showActivityAlert = (activity) => {
    const { t } = this.props;
    const id = activity.activityId || activity.id;
    this._alertedActivityIds.add(id);
    this.setState((prev) =>
      prev.alertQueue.includes(id)
        ? null
        : { alertQueue: [...prev.alertQueue, id] }
    );
    // Trace de l'affichage, écrite une seule fois (idempotent côté connecteur)
    if (!activity.webNotifiedAt) activityConnect._markActivityWebNotified(id);

    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        const notif = new Notification(t("activities.webNotif.title"), {
          body: activity.title,
          icon: "/favicon.ico",
          tag: `activity_${id}`,
        });
        notif.onclick = () => {
          window.focus();
          notif.close();
        };
      } catch (e) {
        // Notification constructor can throw on some platforms — the overlay card still shows
      }
    }
  };

  removeFromAlertQueue = (id) =>
    this.setState((prev) => ({
      alertQueue: prev.alertQueue.filter((queuedId) => queuedId !== id),
    }));

  getAlertActivities = () =>
    this.state.alertQueue
      .map((id) =>
        this.state.activities.find((a) => (a.activityId || a.id) === id)
      )
      .filter(Boolean);

  completeFromAlert = async (activity) => {
    const id = activity.activityId || activity.id;
    this.removeFromAlertQueue(id);
    await activityConnect._completeActivity(id);
  };

  snoozeFromAlert = async (activity) => {
    const id = activity.activityId || activity.id;
    this.removeFromAlertQueue(id);
    await activityConnect._updateActivity(id, {
      dueAt: moment().add(1, "day").set({ hour: 9, minute: 0, second: 0 }).toDate(),
    });
  };

  dismissAlert = async (activity) => {
    const id = activity.activityId || activity.id;
    this.removeFromAlertQueue(id);
    await activityConnect._dismissActivityWebAlert(id);
  };

  dismissAllAlerts = async () => {
    const toDismiss = this.getAlertActivities();
    this.setState({ alertQueue: [] });
    for (const activity of toDismiss) {
      await activityConnect._dismissActivityWebAlert(
        activity.activityId || activity.id
      );
    }
  };

  activityDueMoment = (activity) => {
    if (activity.dueAt && activity.dueAt.toDate) {
      return moment(activity.dueAt.toDate());
    }
    if (activity.dueAtUnix) return moment.unix(activity.dueAtUnix);
    return null;
  };

  getOpenActivities = () => this.state.activities.filter((a) => a.status === "open");

  // Activities that count in the badge: overdue or due today (planned future
  // reminders stay quiet, Odoo-style)
  getDueActivities = () => {
    const dayEnd = moment().endOf("day");
    return this.getOpenActivities().filter((a) => {
      const due = this.activityDueMoment(a);
      return due && due.isSameOrBefore(dayEnd);
    });
  };

  getOverdueActivities = () =>
    this.getOpenActivities().filter((a) => {
      const due = this.activityDueMoment(a);
      return due && due.isBefore(moment());
    });

  completeActivity = async (activity) => {
    await activityConnect._completeActivity(activity.activityId || activity.id);
  };

  deleteActivity = async (activity) => {
    await activityConnect._deleteActivity(activity.activityId || activity.id);
  };

  openActivityModal = (activity = null) => {
    this.setState({ activityModalOpen: true, editingActivity: activity });
  };

  closeActivityModal = () => {
    this.setState({ activityModalOpen: false, editingActivity: null });
  };

  requestNotifPermission = async () => {
    if (typeof Notification === "undefined") return;
    await Notification.requestPermission();
    this.forceUpdate(); // re-render the permission row with the new state
  };

  // Visible opt-in for desktop reminders: shown while the browser
  // permission is not granted (complements the automatic ask on first
  // reminder creation, which the user may have missed or dismissed)
  renderNotifPermissionRow = () => {
    const { t } = this.props;
    if (typeof Notification === "undefined") return null;
    if (Notification.permission === "granted") return null;

    if (Notification.permission === "denied") {
      return (
        <div className="px-3 py-1">
          <small className="text-muted">
            <i className="fas fa-bell-slash mr-1"></i>
            {t("activities.webNotif.blocked")}
          </small>
        </div>
      );
    }

    return (
      <div
        className="px-3 py-1"
        style={{ cursor: "pointer" }}
        onClick={this.requestNotifPermission}
      >
        <small className="text-primary font-weight-bold">
          <i className="fas fa-bell mr-1"></i>
          {t("activities.webNotif.enable")}
        </small>
      </div>
    );
  };

  renderActivityItem = (activity) => {
    const { t, activitiesDestination } = this.props;
    const due = this.localizedMoment(this.activityDueMoment(activity));
    const isOverdue = due && due.isBefore(moment());
    // Nouvelle interface : lien interne vers l'élément lié (jamais le domaine
    // écrit dans actionUrl, qui peut être celui de la production).
    const v2Destination =
      typeof activitiesDestination === "string" && activitiesDestination.startsWith("/v2")
        ? activityDestination(activity)
        : null;

    return (
      <div
        key={activity.activityId || activity.id}
        className="px-3 py-2 d-flex align-items-center"
        style={{ gap: 10 }}
      >
        <button
          type="button"
          title={t("activities.item.markDone")}
          onClick={() => this.completeActivity(activity)}
          style={{
            width: 20,
            height: 20,
            minWidth: 20,
            borderRadius: "50%",
            border: "1.5px solid #b7b9cc",
            backgroundColor: "white",
            cursor: "pointer",
            padding: 0,
          }}
        />
        <div
          style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
          onClick={() => this.openActivityModal(activity)}
          title={t("activities.item.edit")}
        >
          <div
            className="small font-weight-bold"
            style={{
              color: "#3a3b45",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {activity.title}
          </div>
          <small style={{ color: isOverdue ? "#e74a3b" : "#858796" }}>
            <i className="far fa-clock mr-1"></i>
            {due ? due.fromNow() : ""}
            {activity.linkedEntity && activity.linkedEntity.name && (
              <span className="ml-2">
                <i className="fas fa-link mr-1"></i>
                {activity.linkedEntity.name}
              </span>
            )}
          </small>
        </div>
        {v2Destination && (
          <Link
            to={v2Destination}
            title={t("activities.item.open")}
            style={{ color: "#4e73df", fontSize: "12px" }}
            onClick={this.closeDropdown}
          >
            <i className="fas fa-external-link-alt"></i>
          </Link>
        )}
        {!v2Destination && activity.linkedEntity && activity.linkedEntity.actionUrl && (
          <a
            href={activity.linkedEntity.actionUrl}
            title={t("activities.item.open")}
            style={{ color: "#4e73df", fontSize: "12px" }}
            onClick={this.closeDropdown}
          >
            <i className="fas fa-external-link-alt"></i>
          </a>
        )}
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          title={t("common.delete")}
          onClick={() => this.deleteActivity(activity)}
          style={{ color: "#b7b9cc", fontSize: "12px" }}
        >
          <i className="fas fa-trash-alt"></i>
        </button>
      </div>
    );
  };

  renderActivitiesSection = () => {
    const { activitiesDestination = "/MyActivitiesView", t } = this.props;
    const open = this.getOpenActivities();
    const overdueCount = this.getOverdueActivities().length;
    const { activitiesExpanded } = this.state;
    const toShow = activitiesExpanded ? open : open.slice(0, 4);
    const hiddenCount = open.length - toShow.length;

    return (
      <div className="border-bottom pb-1">
        <div className="px-3 py-2 d-flex justify-content-between align-items-center bg-light">
          <strong style={{ color: "#5a5c69", fontSize: "13px" }}>
            <i className="fas fa-stopwatch mr-2"></i>
            <Link
              to={activitiesDestination}
              onClick={this.closeDropdown}
              style={{ color: "#5a5c69" }}
              title={t("activities.section.seeAll")}
            >
              {t("activities.section.title")}
            </Link>
            {overdueCount > 0 && (
              <Badge color="danger" pill className="ml-2">
                {overdueCount}
              </Badge>
            )}
          </strong>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary py-0 px-2"
            onClick={() => this.openActivityModal(null)}
            style={{ fontSize: "12px" }}
          >
            <i className="fas fa-plus mr-1"></i>
            {t("activities.section.add")}
          </button>
        </div>
        {this.renderNotifPermissionRow()}
        {open.length === 0 ? (
          <div className="px-3 py-2">
            <small className="text-muted">{t("activities.section.empty")}</small>
          </div>
        ) : (
          <>
            {toShow.map((a) => this.renderActivityItem(a))}
            {hiddenCount > 0 && (
              <div
                className="text-center pb-1"
                style={{ cursor: "pointer" }}
                onClick={() => this.setState({ activitiesExpanded: true })}
              >
                <small className="text-primary font-weight-bold">
                  <i className="fas fa-chevron-down mr-1"></i>
                  {t("activities.section.showMore", { count: hiddenCount })}
                </small>
              </div>
            )}
          </>
        )}
        <div className="text-center pb-1">
          <Link
            to={activitiesDestination}
            onClick={this.closeDropdown}
            style={{ fontSize: "12px" }}
          >
            {t("activities.section.seeAll")}
          </Link>
        </div>
      </div>
    );
  };

  render() {
    const { notificationsDestination = "/MyNotificationsView", t } = this.props;
    const unread = this.getUnread();
    const unreadCount = unread.length;
    // Non lues, puis notifications acquittées par « Me le rappeler » dont le
    // rappel est ouvert et qui n'ont pas encore été converties en tâche
    const rows = buildBellNotificationRows(
      this.state.notifications,
      this.state.activities,
      8
    );
    const dueActivitiesCount = this.getDueActivities().length;
    const overdueActivitiesCount = this.getOverdueActivities().length;
    const badgeCount = unreadCount + dueActivitiesCount;

    return (
      <Dropdown
        nav
        inNavbar
        isOpen={this.state.dropdownOpen}
        toggle={this.toggleDropdown}
      >
        <DropdownToggle aria-label={t("navigationV2.header.notesAndReminders")} nav className="position-relative" style={{ padding: "0.5rem 1rem" }}>
          <em aria-hidden="true" className="fas fa-bell" style={{ fontSize: "1.3rem", color: "white" }}></em>
          {badgeCount > 0 && (
            <Badge
              color={overdueActivitiesCount > 0 || unreadCount > 0 ? "danger" : "warning"}
              pill
              style={{
                position: "absolute",
                top: "2px",
                right: "2px",
                fontSize: "0.7rem",
                minWidth: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #fff",
              }}
            >
              {badgeCount > 99 ? "99+" : badgeCount}
            </Badge>
          )}
        </DropdownToggle>

        <DropdownMenu
          style={{
            width: "380px",
            maxHeight: "520px",
            overflowY: "auto",
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            marginTop: "10px",
            right: "0",
            left: "auto",
            transform: "translateX(-10px)",
          }}
        >
          {/* Personal activities ("Mes rappels") */}
          {this.renderActivitiesSection()}

          {/* Header */}
          <div className="px-3 py-2 border-bottom bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 font-weight-bold">
                <i className="fas fa-bell mr-2"></i>
                {t("noteNotifications.title")}
                {unreadCount > 0 && (
                  <Badge color="danger" className="ml-2">
                    {unreadCount}
                  </Badge>
                )}
              </h6>
              <Link
                to={notificationsDestination}
                className="btn btn-sm btn-primary"
                onClick={this.closeDropdown}
              >
                {t("noteNotifications.seeAll")}
              </Link>
            </div>
          </div>

          {/* List */}
          {rows.length === 0 ? (
            <div className="text-center py-4" style={{ color: "#858796" }}>
              <i className="fas fa-check-circle mb-2" style={{ fontSize: "1.8rem", opacity: 0.4 }}></i>
              <div style={{ fontSize: "13px" }}>{t("noteNotifications.empty")}</div>
            </div>
          ) : (
            rows.map(({ notification: n, hasOpenReminder, canConvertToTask }) => (
              <div key={n.id} className="px-3 py-2 border-bottom">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span style={{ fontWeight: 600, color: "#5a5c69", fontSize: "13px" }}>
                    {n.noteAuthorName || t("myNotifications.unknownAuthor")}
                  </span>
                  <span className="d-flex align-items-center" style={{ gap: 8 }}>
                    <span style={{ color: "#858796", fontSize: "11px" }}>
                      {this.formatDate(n)}
                    </span>
                    {hasOpenReminder ? (
                      <span
                        title={t("activities.section.title")}
                        style={{ color: "#858796", fontSize: "12px" }}
                      >
                        <i className="fas fa-stopwatch"></i>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        title={t("activities.remindMe")}
                        onClick={() => this.setState({ remindNotif: n })}
                        style={{ color: "#4e73df", fontSize: "12px" }}
                      >
                        <i className="fas fa-stopwatch"></i>
                      </button>
                    )}
                    {canConvertToTask && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        title={t("activities.convert.toTask")}
                        onClick={() => this.setState({ taskNotif: n })}
                        style={{ color: "#4e73df", fontSize: "12px" }}
                      >
                        <i className="fas fa-clipboard-list"></i>
                      </button>
                    )}
                  </span>
                </div>
                <div
                  style={{
                    color: "#3a3b45",
                    fontSize: "13px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {n.noteContent || ""}
                </div>
                {n.sectorName && (
                  <div style={{ color: "#858796", fontSize: "11px", marginTop: 2 }}>
                    <i className="fas fa-layer-group mr-1"></i>
                    {n.sectorName}
                  </div>
                )}
              </div>
            ))
          )}

          {/* Footer */}
          {unreadCount > 0 && (
            <div className="px-3 py-2 text-center">
              <Button
                color="primary"
                outline
                size="sm"
                block
                disabled={this.state.acknowledging}
                onClick={this.acknowledgeAll}
              >
                <i className="fas fa-check-double mr-1"></i>
                {t("noteNotifications.acknowledgeAll")}
              </Button>
            </div>
          )}
        </DropdownMenu>

        {/* Quick-add / edit modal for personal activities */}
        <ActivityQuickAddModal
          key={
            this.state.editingActivity
              ? this.state.editingActivity.activityId || this.state.editingActivity.id
              : "new"
          }
          isOpen={this.state.activityModalOpen}
          onClose={this.closeActivityModal}
          activity={this.state.editingActivity}
        />

        {/* "Me le rappeler" : convert a notification into a personal activity */}
        {this.state.remindNotif && (
          <ActivityQuickAddModal
            key={`remind_${this.state.remindNotif.id}`}
            isOpen={true}
            onClose={() => this.setState({ remindNotif: null })}
            {...buildActivityPrefillFromNotification(this.state.remindNotif, t)}
            onSaved={() =>
              notifConnect.acknowledgeNotifications([this.state.remindNotif.id])
            }
          />
        )}

        {/* Due-reminder cards: persistent stacked overlay */}
        <ActivityAlertsOverlay
          activities={this.getAlertActivities()}
          t={t}
          lang={this.getLang()}
          onComplete={this.completeFromAlert}
          onSnooze={this.snoozeFromAlert}
          onDismiss={this.dismissAlert}
          onDismissAll={this.dismissAllAlerts}
        />

        {/* "Convertir en tâche" : dispatch a notification to the team */}
        {this.state.taskNotif && (
          <React.Suspense fallback={null}>
            <LazyTaskCreationModal
              key={`task_${this.state.taskNotif.id}`}
              isOpen={true}
              toggle={() => this.setState({ taskNotif: null })}
              {...buildTaskPrefillFromNotification(this.state.taskNotif, t)}
              onTaskCreated={() => {
                const converted = this.state.taskNotif;
                if (converted) {
                  if (converted.status !== "acknowledged") {
                    notifConnect.acknowledgeNotifications([converted.id]);
                  }
                  // Le rappel issu de cette notification ne propose plus la conversion
                  activityConnect._markActivitiesConvertedFromNotification(converted.id);
                }
                this.setState({ taskNotif: null });
              }}
            />
          </React.Suspense>
        )}
      </Dropdown>
    );
  }
}

export default withNamespaces()(NoteNotificationDropdown);
